import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import * as yup from "yup";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { InventoryPermissions } from "src/constants/permissions";
import {
  convertObjectToFormData,
  specificBranchHasPermission,
} from "src/utils";
import { DatePicker } from "@mui/x-date-pickers";
import {
  useGetBranches,
  useGetCurrencies,
  useGetPaymentMethods,
} from "src/api/generic";
import { useGetClientsList } from "src/api/sales/clients";
import { useGetMenuProducts } from "src/api/inventory/menu";
import {
  useAddInvoiceMutation,
  useGetInvoiceById,
  useUpdateInvoiceMutation,
} from "src/api/sales/invoices";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import AddMoreIcon from "src/assets/svg-icons/settings/add_more.svg";
import { authState } from "src/store/auth";
import PaymentsTable from "./payments-table";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import { useGetZatcaStatus } from "src/api/integrations/zatca";

const schema = yup.object().shape({
  type: yup
    .string()
    .required("required")
    .oneOf(["invoice", "credit_note"] as const),
  branch_id: yup.number().required("Required"),
  client_id: yup.number().required("Required"),
  is_simplified: yup
    .string()
    .required("Required")
    .oneOf(["yes", "no"] as const),
  date: yup.date().when("type", {
    is: "invoice",
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  invoice_date: yup.date().required("Required"),
  currency_id: yup.number().required("Required"),
  discount: yup.string().required("Required"),
  note: yup.string().nullable(),

  items: yup
    .array()
    .of(
      yup.object().shape({
        menu_name: yup.string().required("Required"),
        menu_id: yup.string().required("Required"),
        branch_id: yup.string().required("Required"),
        unit_cost: yup.string().required("Required"),
        tax_rate: yup.string().required("Required"),
        quantity: yup
          .string()
          .required("Required")
          .test(
            "positive-quantity",
            "Must be greater than 0",
            (value) => Number(value) > 0,
          ),
        discount_percentage: yup.string(),
        price: yup.string().required("Required"),
        // for returned credit note
        returnedQnty: yup
          .string()
          .test(
            "returned-quantity",
            "Returned quantity cannot exceed quantity",
            function (value) {
              const root = this.from?.[this.from.length - 1]?.value;
              const isCreditNote = root?.type === "credit_note";
              if (!isCreditNote) {
                return true;
              }

              if (!value) {
                return this.createError({
                  message: "Required",
                });
              }

              if (Number(value) <= 0) {
                return this.createError({
                  message: "Must be greater than 0",
                });
              }

              return Number(value) <= Number(this.parent.quantity);
            },
          ),

        isInventoryReturned: yup
          .boolean()
          .test("inventory-returned", "Required", function (value) {
            const root = this.from?.[this.from.length - 1]?.value;
            const isCreditNote = root?.type === "credit_note";

            if (!isCreditNote) {
              return true;
            }

            return value !== undefined && value !== null;
          }),
      }),
    )
    .min(1, "At least one item is required")
    .required("Required"),

  payments: yup
    .array()
    .of(
      yup.object().shape({
        payment_method_id: yup
          .string()
          .test(
            "payment-method-required",
            "Payment method is required when amount is specified",
            function (value) {
              const { amount } = this.parent;
              if (amount && !value) {
                return false;
              }
              return true;
            },
          ),
        amount: yup
          .string()
          .test(
            "payment-amount-required",
            "Payment amount is required when payment method is selected",
            function (value) {
              const { payment_method_id } = this.parent;
              if (payment_method_id && !value) {
                return false;
              }
              return true;
            },
          )
          .test("min-value", "Must be greater than 0", (value) => {
            if (!value) return true; // Skip validation if the value is empty
            return parseFloat(value) > 0;
          }),
      }),
    )
    .required("Required"),

  files: yup.array().of(yup.mixed()).nullable(),
  terms: yup.array().of(yup.string()),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const AddEditInvoice = () => {
  const navigate = useNavigate();
  const { t: generalT } = useTranslation("general");
  const { t } = useTranslation("inventory");
  const { t: salesT } = useTranslation("addSales");
  const { t: purchasingT } = useTranslation("purchasing");
  const { t: profileT } = useTranslation("profile");
  const { id } = useParams();
  const auth = useRecoilValue(authState);
  const { user } = auth;
  const company = user.company ?? {};
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(true);
  const [files, setFiles] = useState<any>([]);
  const [isSaveAsDraft, setIsSaveAsDraft] = useState<boolean>(false);

  const mode = !!id ? "edit" : "add";

  // APIS
  const {
    data: invoiceData,
    status: getCountByIdStatus,
    refetch,
    isFetching,
    isLoading,
  } = useGetInvoiceById({
    enabled: !!id,
    id: id as string,
  });
  const { data: branchesData } = useGetBranches();
  const { data: clientsData } = useGetClientsList();
  const { data: currenciesData } = useGetCurrencies();
  const { mutate, error, status, isPending, data } = useAddInvoiceMutation();
  const {
    mutate: mutateUpdate,
    error: errorUpdate,
    status: statusUpdate,
    isPending: isPendingUpdate,
  } = useUpdateInvoiceMutation();
  const { data: paymentsData } = useGetPaymentMethods();
  const { data: statusData } = useGetZatcaStatus();
  const isConnectedToZatca = statusData?.data.fai.connected;

  const invoiceStatus = invoiceData?.data?.invoice_status;
  const zatcaStatus = invoiceData?.data?.zatca?.status;
  const isSubmitted = invoiceStatus === "submitted";
  const isPendingStatus = invoiceStatus === "pending";
  const isCancelled = invoiceStatus === "cancelled";
  const isZatcsRejected = zatcaStatus === "rejected";

  const isDisabled =
    isSubmitted || isCancelled || isPendingStatus || isZatcsRejected;

  const isCreditNote = invoiceData?.data?.type === "credit_note";

  const HeadText = [
    generalT("item"),
    generalT("inventory"),
    `${salesT("taxRate")} %`,
    generalT("qty"),
    generalT("price"),
    generalT("amount"),
    `${generalT("discount")} %`,
    ...(isCreditNote
      ? [salesT("returnedQnty"), salesT("returnedToInventory")]
      : []),
    generalT("tax"),
    generalT("total"),
    ...(!isCreditNote && !isDisabled ? [generalT("actions")] : []),
  ];

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema, {
      context: {
        isCreditNote,
      },
    }),
    defaultValues: {
      type: "invoice",
      is_simplified: "yes",
      discount: "0",
      note: "",
      currency_id: 1,
      payments: [
        {
          payment_method_id: "",
          amount: "",
        },
      ],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    name: "items",
    control,
  });

  const {
    fields: paymentsFields,
    append: appendPayment,
    remove: removePayment,
  } = useFieldArray({
    name: "payments",
    control,
  });

  const { data: menusData } = useGetMenuProducts({
    branch_id: watch("branch_id"),
  });

  const selectedClient = clientsData?.data.find((client: any) => {
    return client.id === +watch("client_id");
  });

  const selectedBranch = branchesData?.data.find((branch) => {
    return branch.id === +watch("branch_id");
  });

  const isSaudiBranch = selectedBranch?.country?.id === 1;

  // permissions
  const confirmPermission = specificBranchHasPermission(
    InventoryPermissions.confirmStocktaking,
    invoiceData?.data.branch.id,
  );
  const addPermission = specificBranchHasPermission(
    InventoryPermissions.addStocktaking,
    invoiceData?.data.branch.id,
  );

  const branchesHavePermission =
    branchesData?.data.filter(
      (branch) =>
        branch.status &&
        specificBranchHasPermission(
          InventoryPermissions.addStocktaking,
          branch.id,
        ),
    ) || [];

  let totalAmountBeforeDiscount = 0;
  let returnedTotalAmountBeforeDiscount = 0;

  let subTotal = 0;
  let returnedSubTotal = 0;

  let allTax = 0;
  let returnedAllTax = 0;

  watch("items")?.forEach((item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const taxRate = Number(item.tax_rate) || 0;
    const itemDiscountRate = Number(item.discount_percentage) || 0;

    // Original invoice calc
    const amount = qty * price;
    totalAmountBeforeDiscount += amount;
    const amountAfterDiscount = amount * (1 - itemDiscountRate / 100);
    subTotal += amountAfterDiscount;
    const tax = amountAfterDiscount * (taxRate / 100);
    allTax += tax;
  });

  const allDiscount = Number(watch("discount")) ?? 0;

  const allDiscountPercentage =
    ((Number(watch("discount")) ?? 0) / subTotal) * 100;

  const allTaxAfterDiscount = allTax * (1 - (allDiscountPercentage ?? 0) / 100);

  const allTotal = subTotal + allTaxAfterDiscount;

  const allTotalAfterDiscount = allTotal - allDiscount;

  // credit note calculations
  watch("items")?.forEach((item) => {
    if (isCreditNote) {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      const taxRate = Number(item.tax_rate) || 0;
      const itemDiscountRate = Number(item.discount_percentage) || 0;

      // Original invoice
      const amount = qty * price;
      const amountAfterDiscount = amount * (1 - itemDiscountRate / 100);
      const tax = amountAfterDiscount * (taxRate / 100);

      // Returned credit note invoice
      const returnedQty = Number(item.returnedQnty) || 0;
      const returnedAmount = returnedQty * price;
      returnedTotalAmountBeforeDiscount += returnedAmount;

      const returnedAmountAfterDiscount =
        returnedAmount * (1 - itemDiscountRate / 100);

      returnedSubTotal += returnedAmountAfterDiscount;

      const returnedTax = returnedAmountAfterDiscount * (taxRate / 100);
      returnedAllTax += returnedTax;
    }
  });

  const returnedAllDiscount = Number(watch("discount")) ?? 0;

  const returnedAllDiscountPercentage =
    ((Number(watch("discount")) ?? 0) / returnedSubTotal) * 100;

  const returnedAllTaxAfterDiscount =
    returnedAllTax * (1 - (returnedAllDiscountPercentage ?? 0) / 100);

  const returnedAllTotalAfterDiscount =
    returnedSubTotal + returnedAllTaxAfterDiscount - returnedAllDiscount;

  const navigateToInvoices = () => {
    navigate("/sales/invoices");
  };

  // Handler function when item is selected
  const handleItemSelect = (event: React.SyntheticEvent, newValue: any) => {
    if (!validateLockDate()) return;
    setSelectedItem(newValue);
    if (newValue) {
      append({
        menu_name: newValue?.name,
        menu_id: newValue?.id,
        branch_id: `${selectedBranch?.id}`,
        unit_cost: newValue.unit_cost ?? 0,
        tax_rate: `${selectedBranch?.vat ?? ""}`,
        quantity: "",
        discount_percentage: "0",
        price: (
          newValue?.price /
          (1 + (selectedBranch?.vat ?? 0) / 100)
        ).toString(),
      });

      setInputValue("");
      setSelectedItem(null);
    }
  };

  // Handle manual changes to the input field
  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    if (selectedItem === null) {
      setInputValue(newInputValue);
    }
  };

  const validateLockDate = (): boolean => {
    if (
      !!invoiceData?.data?.branch?.close_date &&
      moment(watch("date"))
        .startOf("day")
        .isSameOrBefore(
          moment(invoiceData?.data?.branch?.close_date).startOf("day"),
        )
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("canNotAddEditOrDeleteBeforeLockDate")}: ${
            invoiceData?.data?.branch?.close_date
          }`,
        },
      ]);
      return false;
    }
    return true;
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
  };

  const handleAppendPayment = () => {
    appendPayment({
      payment_method_id: "",
      amount: "",
    });
  };

  const handleRemovePayment = (index: number) => {
    removePayment(index);
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    // Lock date validation
    if (!validateLockDate()) return;

    const {
      branch_id,
      client_id,
      items,
      payments,
      date,
      invoice_date,
      is_simplified,
      note,
      terms = [],
      type,
      currency_id,
    } = data;

    if (!isSaveAsDraft && is_simplified === "no") {
      const missingFields: string[] = [];

      if (!selectedClient?.phone) missingFields.push(profileT("phoneNumber"));
      if (!selectedClient?.vat)
        missingFields.push(generalT("taxRegistrationNumber"));
      if (!selectedClient?.street) missingFields.push(profileT("street"));
      if (!selectedClient?.building_number)
        missingFields.push(profileT("buildingNumber"));

      if (missingFields.length > 0) {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: `${salesT("missingClientData")}: ${missingFields.join(", ")}`,
          },
        ]);
        return;
      }
    }

    const { totalItemUnitCost, totalItemDiscount } = items.reduce(
      (acc, item) => {
        const qnty = isCreditNote ? item.returnedQnty : item.quantity;

        const itemTotalUnitCost =
          (Number(qnty) || 0) * (Number(item.unit_cost) || 0);

        const amount = (Number(qnty) || 0) * (Number(item.price) || 0);

        const itemDiscount =
          amount * ((Number(item.discount_percentage) || 0) / 100);

        acc.totalItemUnitCost += itemTotalUnitCost;
        acc.totalItemDiscount += itemDiscount;

        return acc;
      },
      {
        totalItemUnitCost: 0,
        totalItemDiscount: 0,
      },
    );

    console.log({ totalItemDiscount, totalItemUnitCost });

    const globalDiscount = isCreditNote
      ? returnedAllDiscount
      : (allDiscount ?? 0);

    const dataToSendWithoutArrays = {
      branch_id,
      client_id,
      currency_id,
      type,
      amount_before_discount: isCreditNote
        ? returnedTotalAmountBeforeDiscount
        : totalAmountBeforeDiscount,
      amount: isCreditNote ? returnedSubTotal : subTotal,
      total_unit_cost: totalItemUnitCost,
      discount: globalDiscount,
      total_discount: globalDiscount + totalItemDiscount,
      total: isCreditNote
        ? returnedAllTotalAfterDiscount
        : allTotalAfterDiscount,
      tax: isCreditNote ? returnedAllTaxAfterDiscount : allTaxAfterDiscount,
      invoice_status: isSaveAsDraft
        ? "draft"
        : isConnectedToZatca && selectedBranch?.country?.id === 1
          ? "pending"
          : "submitted",
      date: moment(date).format("YYYY-MM-DD"),
      invoice_date: moment(invoice_date).format("YYYY-MM-DD"),
      is_simplified: is_simplified === "yes" ? 1 : 0,
      note: note ?? "",
    };

    if (mode === "add") {
      const formDataToSend = convertObjectToFormData(dataToSendWithoutArrays);

      // append items
      if (items.length > 0) {
        items.forEach((item, index: number) => {
          const amount =
            (Number(item.quantity) || 0) * (Number(item.price) || 0);
          const discount =
            amount * (Number(item.discount_percentage) / 100 || 0);
          const amountAfterDiscount =
            amount * (1 - (Number(item.discount_percentage) / 100 || 0));

          const ItemGlobalDiscount =
            allDiscount > 0 && subTotal > 0
              ? (amountAfterDiscount / subTotal) * allDiscount
              : 0;

          const finalItemAmount = amountAfterDiscount - ItemGlobalDiscount;

          const tax = finalItemAmount * (Number(item.tax_rate) / 100 || 0);

          const total = finalItemAmount + tax;

          const itemTotalUnitCost =
            (Number(item.quantity) || 0) * (Number(item.unit_cost) || 0);

          formDataToSend.append(`items[${index}][menu_id]`, item.menu_id);
          formDataToSend.append(`items[${index}][branch_id]`, item.branch_id);
          formDataToSend.append(`items[${index}][unit_cost]`, item.unit_cost);
          formDataToSend.append(`items[${index}][tax_rate]`, item.tax_rate);
          formDataToSend.append(`items[${index}][quantity]`, item.quantity);
          formDataToSend.append(
            `items[${index}][discount_percentage]`,
            item.discount_percentage ?? "0",
          );
          formDataToSend.append(
            `items[${index}][discount_amount]`,
            `${discount}`,
          );
          formDataToSend.append(`items[${index}][price]`, item.price);
          formDataToSend.append(
            `items[${index}][amount]`,
            `${finalItemAmount}`,
          );

          formDataToSend.append(
            `items[${index}][total_unit_cost]`,
            `${itemTotalUnitCost}`,
          );
          formDataToSend.append(`items[${index}][tax]`, `${tax}`);
          formDataToSend.append(`items[${index}][total]`, `${total}`);
        });
      }

      // append payments array
      payments.forEach((payment, index: number) => {
        if (
          payment.payment_method_id &&
          payment.amount &&
          +payment?.amount > 0
        ) {
          formDataToSend.append(
            `payments[${index}][payment_method_id]`,
            payment.payment_method_id,
          );
          formDataToSend.append(`payments[${index}][amount]`, payment.amount);
        }
      });

      // append files
      if (files.length > 0) {
        files.forEach((file: any, index: number) => {
          formDataToSend.append(`files[${index}]`, file);
        });
      }

      // append terms
      if (terms.length > 0) {
        terms.forEach((term: any, index: number) => {
          formDataToSend.append(`terms[${index}]`, term);
        });
      }

      mutate(formDataToSend);
    } else {
      mutateUpdate({
        id,
        ...dataToSendWithoutArrays,
        payments: payments,
        terms: terms,
        items: items.map((item) => {
          const amount =
            (Number(item.quantity) || 0) * (Number(item.price) || 0);

          const discount =
            amount * (Number(item.discount_percentage) / 100 || 0);

          const amountAfterDiscount =
            amount * (1 - (Number(item.discount_percentage) / 100 || 0));

          const ItemGlobalDiscount =
            allDiscount > 0 && subTotal > 0
              ? (amountAfterDiscount / subTotal) * allDiscount
              : 0;

          const finalItemAmount = amountAfterDiscount - ItemGlobalDiscount;

          const tax = finalItemAmount * (Number(item.tax_rate) / 100 || 0);

          const total = finalItemAmount + tax;

          const itemTotalUnitCost =
            (Number(item.quantity) || 0) * (Number(item.unit_cost) || 0);

          // for credit note
          const returnAmount =
            (Number(item.returnedQnty) || 0) * (Number(item.price) || 0);

          const returnAmountAfterDiscount =
            returnAmount * (1 - (Number(item.discount_percentage) / 100 || 0));

          const returnItemGlobalDiscount =
            returnedAllDiscount > 0 && returnedSubTotal > 0
              ? (amountAfterDiscount / returnedSubTotal) * returnedAllDiscount
              : 0;

          const returnFinalItemAmount =
            returnAmountAfterDiscount - returnItemGlobalDiscount;

          const returnTax =
            returnFinalItemAmount * (Number(item.tax_rate) / 100 || 0);

          const returnTotal = returnFinalItemAmount + returnTax;

          const returnedItemTotalUnitCost =
            (Number(item.returnedQnty) || 0) * (Number(item.unit_cost) || 0);

          return {
            branch_id: item.branch_id,
            menu_id: item.menu_id,
            unit_cost: Number(item.unit_cost),
            price: item.price,
            tax_rate: item.tax_rate,
            quantity: item.quantity,
            discount_percentage: item.discount_percentage,
            discount_amount: discount,
            amount: isCreditNote ? returnFinalItemAmount : finalItemAmount,
            tax: isCreditNote ? returnTax : tax,
            total: isCreditNote ? returnTotal : total,
            returned_quantity: item.returnedQnty || 0,
            is_inventory: item.isInventoryReturned,
            total_unit_cost: isCreditNote
              ? returnedItemTotalUnitCost
              : itemTotalUnitCost,
          };
        }),
      });
    }
  };

  useEffect(() => {
    if (getCountByIdStatus === "success") {
      if (isSaving && !isFetching) {
        reset({
          type: invoiceData?.data?.type,
          branch_id: invoiceData.data?.branch?.id,
          client_id: invoiceData.data?.client?.id,
          is_simplified: invoiceData?.data?.is_simplified ? "yes" : "no",
          currency_id: invoiceData.data?.currency?.id,
          discount: invoiceData.data.discount,
          terms: invoiceData.data.terms?.map((term: any) => term.id) || [],
          note: invoiceData.data.note ?? "",
          date: moment(invoiceData.data.date).toDate(),
          invoice_date: moment(invoiceData.data.invoice_date).toDate(),
          items: invoiceData.data.items.map((item: any) => {
            return {
              branch_id: item.branch_id,
              menu_name: item.menu?.name,
              menu_id: item.menu?.id,
              unit_cost: item.unit_cost,
              price: item.price,
              tax_rate: item.tax_rate,
              quantity: item.quantity,
              discount_percentage: item.discount_percentage ?? "0",
              returnedQnty: item.returned_quantity,
              isInventoryReturned: item.is_inventory,
            };
          }),
          payments: invoiceData.data?.payments.map((pay: any) => {
            return {
              payment_method_id: pay.payment_method?.id,
              amount: pay.total,
            };
          }),
        });
        setIsSaving(false);
      }
    }
  }, [getCountByIdStatus, isFetching]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      navigate(
        status === "success" && isSaveAsDraft
          ? `/sales/invoices/${data?.data.sale_invoice_id}`
          : "/sales/invoices",
      );
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  let paymentsCredit = 0;
  watch("payments").forEach((pay) => {
    paymentsCredit += Number(pay.amount) || 0;
  });

  const credit = allTotalAfterDiscount - paymentsCredit || 0;

  const selectedServicesItemsIds = watch("items")?.map((item) => item.menu_id);

  const subTotalToView = isCreditNote ? returnedSubTotal : subTotal;
  const allDiscountToView = isCreditNote ? returnedAllDiscount : allDiscount;
  const allTaxToView = isCreditNote
    ? returnedAllTaxAfterDiscount
    : allTaxAfterDiscount;
  const allTotalAfterDiscountToView = isCreditNote
    ? returnedAllTotalAfterDiscount
    : allTotalAfterDiscount;

  console.log({
    errors,
    allTotalAfterDiscountToView,
    isCreditNote,
    allTotalAfterDiscount,
    allTotal,
    allTax,
    allDiscount,
    subTotal,
  });

  if (isFetching) {
    return (
      <Box
        display={"flex"}
        justifyContent={"center"}
        height={"500px"}
        alignItems={"center"}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth={1320} mx="auto" width="100%">
      <Stack gap={3} p={{ xs: 2, sm: 4 }}>
        <Stack spacing={2} direction={"row"} mb={3} alignItems={"center"}>
          <Typography
            color={"#475467"}
            lineHeight={"19.2px"}
            onClick={navigateToInvoices}
            sx={{ cursor: "pointer" }}
          >
            {generalT("sales")}
          </Typography>
          <img src={RightArrow} alt="" />
          <Typography
            color={"#475467"}
            lineHeight={"19.2px"}
            onClick={navigateToInvoices}
            sx={{ cursor: "pointer" }}
          >
            {salesT("salesInvoices")}
          </Typography>
          <img src={RightArrow} alt="" />
          <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
            <Typography
              color={"#344054"}
              fontWeight={600}
              lineHeight={"19.2px"}
            >
              {mode === "add"
                ? salesT("addNewSalesInvoice")
                : invoiceData?.data.reference}
            </Typography>
          </Box>
        </Stack>

        {isLoading ? (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            minHeight={"250px"}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Stack
                      direction={"row"}
                      gap={1}
                      mb={2}
                      alignItems={"center"}
                    >
                      <Typography>{salesT("billedBy")}</Typography>
                      <Typography fontSize={"13px"} color={"textSecondary"}>
                        {salesT("yourBusinessDetails")}
                      </Typography>
                    </Stack>
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.branch_id}
                    >
                      <InputLabel id="demo-simple-select-label">
                        {generalT("branch")}
                      </InputLabel>
                      <Controller
                        name="branch_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            key={field.value}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label={generalT("branch")}
                            disabled={isDisabled || isCreditNote}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value);

                              remove();
                            }}
                          >
                            {branchesHavePermission?.map(
                              ({ id, name, active_subscriptions }) => {
                                return (
                                  <MenuItem
                                    key={id}
                                    value={`${id}`}
                                    disabled={
                                      !active_subscriptions?.package?.has_menu
                                    }
                                  >
                                    {name}
                                  </MenuItem>
                                );
                              },
                            )}
                          </Select>
                        )}
                      />
                      <FormHelperText id="my-helper-text">
                        {generalT(`${errors.branch_id?.message ?? ""}`)}
                      </FormHelperText>
                    </FormControl>
                    {!!watch("branch_id") && (
                      <Stack
                        p={2}
                        gap={1.5}
                        bgcolor={"#F9FAFB"}
                        borderRadius={"6px"}
                        border={"1px solid #dee3e8"}
                        mt={1}
                      >
                        <Typography
                          color={"#344054"}
                          fontWeight={600}
                          fontSize={"14px"}
                        >
                          {salesT("yourBusinessDetails")}
                        </Typography>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {generalT("name")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {user.company?.name}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {profileT("phoneNumber")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {user.company?.address?.phone}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {generalT("taxRegistrationNumber")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {user.company?.tax_registration_number}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {generalT("address")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {user.company?.address?.street_address},{" "}
                            {user.company?.address?.building_number}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {profileT("country")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {company?.address?.country?.name}
                          </Typography>
                        </Stack>
                        {company?.address?.configuration?.show_postal_code ? (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                          >
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {profileT("postalCode")}
                            </Typography>
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {company?.address?.postal_code}
                            </Typography>
                          </Stack>
                        ) : null}
                        {company?.address?.configuration?.show_city_id ? (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                          >
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {profileT("city")}
                            </Typography>
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {company?.address?.city?.name}
                            </Typography>
                          </Stack>
                        ) : null}
                        {company?.address?.configuration?.show_district ? (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                          >
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {profileT("district")}
                            </Typography>
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {company?.address?.district}
                            </Typography>
                          </Stack>
                        ) : null}
                        {company?.address?.configuration
                          ?.show_secondary_number ? (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                          >
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {profileT("secondaryNumber")}
                            </Typography>
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {company?.address?.secondary_number}
                            </Typography>
                          </Stack>
                        ) : null}
                      </Stack>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack
                      direction={"row"}
                      gap={1}
                      mb={2}
                      alignItems={"center"}
                    >
                      <Typography>{salesT("billedTo")}</Typography>
                      <Typography fontSize={"13px"} color={"textSecondary"}>
                        {salesT("clientDetails")}
                      </Typography>
                    </Stack>
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.client_id}
                    >
                      <InputLabel id="demo-simple-select-label">
                        {salesT("client")}
                      </InputLabel>
                      <Controller
                        name="client_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            key={field.value}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label={salesT("client")}
                            disabled={isDisabled || isCreditNote}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value);
                              const client = clientsData?.data.find(
                                (client: any) => {
                                  return client.id === +value;
                                },
                              );
                              setValue(
                                "terms",
                                client.terms?.map((term: any) => term.id) || [],
                              );
                            }}
                          >
                            {clientsData?.data?.map(({ id, name }: any) => {
                              return (
                                <MenuItem key={id} value={id}>
                                  {name}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      />
                      <FormHelperText id="my-helper-text">
                        {generalT(`${errors.client_id?.message ?? ""}`)}
                      </FormHelperText>
                    </FormControl>
                    {selectedClient && (
                      <Stack
                        p={2}
                        gap={1.5}
                        bgcolor={"#F9FAFB"}
                        borderRadius={"6px"}
                        border={"1px solid #dee3e8"}
                        mt={1}
                      >
                        {isConnectedToZatca ? (
                          <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={3}
                            flexWrap={"wrap"}
                            width={"100%"}
                          >
                            <Typography color={"textSecondary"}>
                              {salesT("simplifiedInvoice")}
                            </Typography>
                            <FormControl
                              fullWidth
                              size="small"
                              error={!!errors.is_simplified}
                              sx={{
                                width: "fit-content",
                                ml: { xs: "auto", sm: "unset" },
                              }}
                            >
                              <Controller
                                name="is_simplified"
                                control={control}
                                render={({ field }) => (
                                  <RadioGroup
                                    {...field}
                                    key={field.value}
                                    row
                                    aria-labelledby="demo-row-radio-buttons-group-label"
                                    name="row-radio-buttons-group"
                                  >
                                    <FormControlLabel
                                      value="yes"
                                      control={
                                        <Radio
                                          icon={<CircleOutlinedIcon />}
                                          checkedIcon={<CheckCircleIcon />}
                                          disabled={isDisabled || isCreditNote}
                                        />
                                      }
                                      label={generalT("yes")}
                                      sx={{ color: "#98A2B3" }}
                                    />
                                    <FormControlLabel
                                      value="no"
                                      control={
                                        <Radio
                                          icon={<CircleOutlinedIcon />}
                                          checkedIcon={<CheckCircleIcon />}
                                          disabled={isDisabled || isCreditNote}
                                        />
                                      }
                                      label={generalT("no")}
                                      sx={{ color: "#98A2B3" }}
                                    />
                                  </RadioGroup>
                                )}
                              />
                              <FormHelperText id="my-helper-text">
                                {errors.is_simplified?.message}
                              </FormHelperText>
                            </FormControl>
                          </Stack>
                        ) : null}

                        <Typography
                          color={"#344054"}
                          fontWeight={600}
                          fontSize={"14px"}
                        >
                          {salesT("clientDetails")}
                        </Typography>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {generalT("name")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {selectedClient?.name}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {profileT("phoneNumber")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {selectedClient?.phone}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {generalT("taxRegistrationNumber")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {selectedClient?.vat}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {salesT("clientBalance")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {selectedClient?.balance.toFixed(2) ?? 0} SAR
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {generalT("address")}
                          </Typography>
                          <Typography fontSize={"14px"} color={"#344054"}>
                            {selectedClient?.building_number ?? ""},{" "}
                            {selectedClient?.street ?? ""}
                          </Typography>
                        </Stack>
                        {selectedClient?.configuration?.show_email && (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                          >
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {generalT("email")}
                            </Typography>
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {selectedClient?.email}
                            </Typography>
                          </Stack>
                        )}

                        {selectedClient?.configuration?.show_zip_code && (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                          >
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {profileT("postalCode")}
                            </Typography>
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {selectedClient?.zip_code}
                            </Typography>
                          </Stack>
                        )}
                        {selectedClient?.configuration?.show_city_id && (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                          >
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {profileT("city")}
                            </Typography>
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {selectedClient?.city?.name}
                            </Typography>
                          </Stack>
                        )}
                        {selectedClient?.configuration?.show_country_id && (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                          >
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {profileT("country")}
                            </Typography>
                            <Typography fontSize={"14px"} color={"#344054"}>
                              {selectedClient?.country?.name}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="invoice_date"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.invoice_date,
                              helperText: t(
                                `${errors.invoice_date?.message ?? ""}`,
                              ),
                              size: "small",
                              label: isCreditNote
                                ? salesT("returnDate")
                                : generalT("invoiceDate"),
                            },
                          }}
                          format={"dd/MM/yyyy"}
                          disabled={isDisabled}
                        />
                      )}
                    />
                  </Grid>
                  {!isCreditNote && (
                    <Grid item xs={12} md={4}>
                      <Controller
                        name="date"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.date,
                                helperText: t(`${errors.date?.message ?? ""}`),
                                size: "small",
                                label: purchasingT("dueDate"),
                              },
                            }}
                            format={"dd/MM/yyyy"}
                            disabled={isDisabled}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.currency_id}
                    >
                      <InputLabel id="demo-simple-select-label">
                        {salesT("currency")}
                      </InputLabel>
                      <Controller
                        name="currency_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            key={field.value}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label={salesT("currency")}
                            disabled={isDisabled || isCreditNote}
                          >
                            {currenciesData?.data?.map(({ id, name }: any) => {
                              return (
                                <MenuItem key={id} value={id}>
                                  {name}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      />
                      <FormHelperText id="my-helper-text">
                        {generalT(`${errors.currency_id?.message ?? ""}`)}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>

                <Typography>{salesT("invoiceItems")}</Typography>

                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    overflowX: "auto",
                  }}
                  variant="outlined"
                >
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        {HeadText.map((item) => (
                          <TableCell
                            key={item}
                            sx={{
                              fontSize: "12px",
                              fontWeight: 600,
                              lineHeight: "18px",
                              color: "#fff",
                              bgcolor: "#FF8D3F",
                            }}
                          >
                            {item}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((itemField, index) => {
                        // for original invoice
                        const amount =
                          (Number(watch(`items.${index}`).quantity) || 0) *
                          (Number(watch(`items.${index}`).price) || 0);
                        const amountDiscounted =
                          amount *
                          (1 -
                            (Number(
                              watch(`items.${index}`).discount_percentage,
                            ) / 100 || 0));
                        const tax =
                          amountDiscounted *
                          (Number(watch(`items.${index}`).tax_rate) / 100 || 0);
                        const total = amountDiscounted + tax;
                        // for credit note
                        const reTurnAmount =
                          (Number(watch(`items.${index}`).returnedQnty) || 0) *
                          (Number(watch(`items.${index}`).price) || 0);

                        const reTurnAmountDiscounted =
                          reTurnAmount *
                          (1 -
                            (Number(
                              watch(`items.${index}`).discount_percentage,
                            ) / 100 || 0));
                        const returnTax =
                          reTurnAmountDiscounted *
                          (Number(watch(`items.${index}`).tax_rate) / 100 || 0);

                        const returnTotal = reTurnAmountDiscounted + returnTax;

                        // to view
                        const itemTotalToView = isCreditNote
                          ? returnTotal
                          : total;
                        const itemAmountDiscountedToView = isCreditNote
                          ? reTurnAmountDiscounted
                          : amountDiscounted;
                        const itemTaxToView = isCreditNote ? returnTax : tax;

                        return (
                          <TableRow
                            key={itemField.id}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell>{itemField?.menu_name}</TableCell>
                            {/* branch */}
                            <TableCell>
                              <FormControl
                                fullWidth
                                size="small"
                                error={!!errors.items?.[index]?.branch_id}
                              >
                                <InputLabel id="demo-simple-select-label">
                                  {generalT("inventory")}
                                </InputLabel>
                                <Controller
                                  name={`items.${index}.branch_id`}
                                  control={control}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      key={field.value}
                                      labelId="demo-simple-select-label"
                                      id="demo-simple-select"
                                      label={generalT("inventory")}
                                      sx={{ width: 140, maxWidth: 140 }}
                                      disabled
                                      onChange={(e) => {
                                        const selectedId = e.target.value;
                                        // update branch_id
                                        field.onChange(selectedId);
                                        // update tax rate
                                        const selectedBranch =
                                          branchesHavePermission?.find(
                                            (branch) =>
                                              `${branch.id}` ===
                                              `${selectedId}`,
                                          );
                                        const taxRate =
                                          selectedBranch?.vat ?? 0;
                                        setValue(
                                          `items.${index}.tax_rate`,
                                          `${taxRate}`,
                                          {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                          },
                                        );
                                      }}
                                    >
                                      {branchesHavePermission?.map(
                                        ({
                                          id,
                                          name,
                                          active_subscriptions,
                                        }) => {
                                          return (
                                            <MenuItem
                                              key={id}
                                              value={`${id}`}
                                              disabled={
                                                !active_subscriptions?.package
                                                  .has_items
                                              }
                                            >
                                              {name}
                                            </MenuItem>
                                          );
                                        },
                                      )}
                                    </Select>
                                  )}
                                />
                                <FormHelperText id="my-helper-text">
                                  {generalT(
                                    `${errors.items?.[index]?.branch_id?.message ?? ""}`,
                                  )}
                                </FormHelperText>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              {/* tax rate */}
                              {isSaudiBranch ? (
                                <FormControl
                                  fullWidth
                                  size="small"
                                  error={!!errors.items?.[index]?.tax_rate}
                                  disabled={isDisabled || isCreditNote}
                                >
                                  <InputLabel id="demo-simple-select-label">
                                    {salesT("taxRate")}
                                  </InputLabel>
                                  <Controller
                                    name={`items.${index}.tax_rate`}
                                    control={control}
                                    render={({ field }) => (
                                      <Select
                                        {...field}
                                        key={field.value}
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        label={salesT("taxRate")}
                                        sx={{ width: 140, maxWidth: 140 }}
                                      >
                                        <MenuItem value={`0`}>0</MenuItem>
                                        <MenuItem
                                          value={`${selectedBranch?.vat ?? ""}`}
                                        >
                                          {selectedBranch?.vat ?? ""}
                                        </MenuItem>
                                      </Select>
                                    )}
                                  />
                                  <FormHelperText id="my-helper-text">
                                    {generalT(
                                      `${errors.items?.[index]?.tax_rate?.message ?? ""}`,
                                    )}
                                  </FormHelperText>
                                </FormControl>
                              ) : (
                                <Controller
                                  name={`items.${index}.tax_rate`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      type="number"
                                      label={salesT("taxRate")}
                                      variant="outlined"
                                      {...field}
                                      error={!!errors.items?.[index]?.tax_rate}
                                      helperText={
                                        errors.items?.[index]?.tax_rate?.message
                                      }
                                      fullWidth
                                      size="small"
                                      sx={{ width: 130, maxWidth: 130 }}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <Typography fontSize={"14px"}>
                                              %
                                            </Typography>
                                          </InputAdornment>
                                        ),
                                      }}
                                      disabled={isDisabled || isCreditNote}
                                    />
                                  )}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`items.${index}.quantity`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    type="number"
                                    label={t("qnty")}
                                    variant="outlined"
                                    {...field}
                                    error={!!errors.items?.[index]?.quantity}
                                    helperText={
                                      errors.items?.[index]?.quantity?.message
                                    }
                                    fullWidth
                                    size="small"
                                    sx={{ width: 130, maxWidth: 130 }}
                                    disabled={isDisabled || isCreditNote}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              {Number(itemField.price).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {/* amount */}
                              {itemAmountDiscountedToView.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`items.${index}.discount_percentage`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    type="number"
                                    label={`${generalT("discount")} %`}
                                    variant="outlined"
                                    {...field}
                                    error={
                                      !!errors.items?.[index]
                                        ?.discount_percentage
                                    }
                                    helperText={
                                      errors.items?.[index]?.discount_percentage
                                        ?.message
                                    }
                                    sx={{ width: 130, maxWidth: 130 }}
                                    fullWidth
                                    size="small"
                                    disabled={isDisabled || isCreditNote}
                                  />
                                )}
                              />
                            </TableCell>
                            {isCreditNote && (
                              <>
                                <TableCell>
                                  <Controller
                                    name={`items.${index}.returnedQnty`}
                                    control={control}
                                    render={({ field }) => (
                                      <TextField
                                        type="number"
                                        label={salesT("returnedQnty")}
                                        variant="outlined"
                                        {...field}
                                        error={
                                          !!errors.items?.[index]?.returnedQnty
                                        }
                                        helperText={
                                          errors.items?.[index]?.returnedQnty
                                            ?.message
                                        }
                                        fullWidth
                                        size="small"
                                        sx={{ width: 130, maxWidth: 130 }}
                                        disabled={isDisabled}
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Controller
                                    name={`items.${index}.isInventoryReturned`}
                                    control={control}
                                    render={({ field }) => (
                                      <FormControlLabel
                                        sx={{ mt: 1.5 }}
                                        control={
                                          <Checkbox
                                            {...field}
                                            color="primary"
                                            disabled={isDisabled}
                                          />
                                        }
                                        label={salesT("returnedToInventory")}
                                        checked={field.value}
                                      />
                                    )}
                                  />
                                </TableCell>
                              </>
                            )}

                            <TableCell>{itemTaxToView.toFixed(2)}</TableCell>
                            <TableCell>{itemTotalToView.toFixed(2)}</TableCell>
                            {!isCreditNote ? (
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    handleDeleteItem(index);
                                  }}
                                >
                                  <img
                                    src={DeleteIcon}
                                    alt=""
                                    width={"16px"}
                                    style={{ cursor: "pointer" }}
                                  />
                                </IconButton>
                              </TableCell>
                            ) : null}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {!isDisabled && !isCreditNote ? (
                  <Autocomplete
                    size="small"
                    disablePortal
                    id="combo-box-demo"
                    options={
                      menusData?.data.filter(
                        (item: any) =>
                          !selectedServicesItemsIds?.includes(item.id),
                      ) ?? []
                    }
                    sx={{ width: 255 }}
                    getOptionLabel={(option: any) => option.name}
                    getOptionKey={(option) => `${option.id}`}
                    value={selectedItem}
                    onChange={handleItemSelect}
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    disabled={!watch("branch_id")}
                    renderInput={(params: any) => (
                      <TextField {...params} label={t("searchAndAddItem")} />
                    )}
                  />
                ) : null}

                {/* total summary */}
                <Stack p={2} gap={1.5} bgcolor={"#F9FAFB"}>
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Typography fontSize={"14px"} color={"#344054"}>
                      {generalT("subtotal")}
                    </Typography>
                    <Typography fontSize={"14px"} color={"#344054"}>
                      {subTotalToView.toFixed(2)}
                    </Typography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography fontSize={"14px"} color={"#344054"}>
                      {generalT("discount")}
                    </Typography>
                    <Stack direction={"row"} gap={2} alignItems={"center"}>
                      {/* global discount */}
                      <Controller
                        name="discount"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            fullWidth
                            label={""}
                            size="small"
                            {...field}
                            error={!!errors.discount}
                            helperText={errors.discount?.message}
                            disabled={isDisabled}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  SAR
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                      <Typography fontSize={"14px"} color={"#344054"}>
                        {allDiscountToView.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Typography fontSize={"14px"} color={"#344054"}>
                      {generalT("totalTax")}
                    </Typography>
                    <Typography fontSize={"14px"} color={"#344054"}>
                      {allTaxToView.toFixed(2)}
                    </Typography>
                  </Stack>
                  <Box height={"1px"} bgcolor={"#D0D5DD"} />
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Typography
                      fontSize={"14px"}
                      fontWeight={500}
                      color={"#344054"}
                    >
                      {generalT("total")}
                    </Typography>
                    <Typography fontSize={"14px"} color={"#344054"}>
                      {allTotalAfterDiscountToView.toFixed(2)}
                    </Typography>
                  </Stack>
                </Stack>

                {/* Payments */}
                <Box>
                  {/* list after submitted */}
                  {isDisabled ? (
                    <PaymentsTable
                      list={invoiceData?.data?.payments ?? []}
                      isLoading={isLoading}
                      refetch={refetch}
                    />
                  ) : null}
                  {/* ordinary add or update  */}
                  {!isDisabled &&
                    paymentsFields.map((paymentField, index) => {
                      return (
                        <Stack
                          key={paymentField.id}
                          direction={"row"}
                          spacing={1}
                          alignItems="flex-start"
                        >
                          <FormControl
                            fullWidth
                            size="small"
                            error={
                              errors.payments &&
                              !!errors.payments[index]?.payment_method_id
                            }
                          >
                            <InputLabel id="demo-simple-select-label">
                              {generalT("paymentMethodOptional")}
                            </InputLabel>
                            <Controller
                              name={`payments.${index}.payment_method_id`}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  key={field.value}
                                  labelId="demo-simple-select-label"
                                  id="demo-simple-select"
                                  label={generalT("paymentMethodOptional")}
                                  disabled={mode === "edit"}
                                >
                                  {paymentsData?.data
                                    ?.filter((pay) => pay.status)
                                    .map(({ id, name }) => {
                                      return (
                                        <MenuItem key={id} value={`${id}`}>
                                          {name}
                                        </MenuItem>
                                      );
                                    })}
                                </Select>
                              )}
                            />
                            <FormHelperText id="my-helper-text">
                              {
                                errors.payments?.[index]?.payment_method_id
                                  ?.message
                              }
                            </FormHelperText>
                          </FormControl>

                          <Controller
                            name={`payments.${index}.amount`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                type="number"
                                label={generalT("amount")}
                                variant="outlined"
                                {...field}
                                error={!!errors.payments?.[index]?.amount}
                                helperText={
                                  errors.payments?.[index]?.amount?.message
                                }
                                fullWidth
                                size="small"
                                disabled={mode === "edit"}
                                sx={{
                                  width: 180,
                                }}
                              />
                            )}
                          />

                          {mode === "add" && (
                            <IconButton
                              onClick={() => handleRemovePayment(index)}
                            >
                              <img src={DeleteIcon} alt="" />
                            </IconButton>
                          )}
                        </Stack>
                      );
                    })}
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    {mode === "add" && (
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<img src={AddMoreIcon} alt="" />}
                        onClick={handleAppendPayment}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        {generalT("addAnotherMethod")}
                      </Button>
                    )}

                    <Typography fontSize={"14px"}>
                      {generalT("dueAmount")}: {credit.toFixed(2)}{" "}
                      {generalT("sar")}
                    </Typography>
                  </Stack>
                </Box>

                <Box maxWidth={600}>
                  {/* note */}
                  <Controller
                    name="note"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label={generalT("notesOptional")}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        maxRows={3}
                        {...field}
                        error={!!errors.note}
                        helperText={errors.note?.message}
                        disabled={isDisabled}
                      />
                    )}
                  />
                  <Typography mt={3} mb={1}>
                    {salesT("terms")}
                  </Typography>

                  {/* terms */}
                  <FormControl fullWidth size="small" error={!!errors.terms}>
                    <InputLabel id="terms-label">{salesT("terms")}</InputLabel>

                    <Controller
                      name="terms"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Select
                            {...field}
                            multiple
                            labelId="terms-label"
                            id="terms"
                            label={salesT("terms")}
                            value={field.value || []}
                            disabled={isDisabled}
                            renderValue={(selected) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {(selected as (string | number)[]).map((id) => {
                                  const term = (
                                    selectedClient?.terms ?? []
                                  )?.find((term: any) => term.id === id);

                                  return (
                                    <Chip
                                      key={id}
                                      label={term?.title}
                                      size="small"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {(selectedClient?.terms ?? []).map(
                              ({ id, title }: any) => (
                                <MenuItem key={id} value={id}>
                                  <Checkbox
                                    checked={(field.value || []).includes(id)}
                                  />
                                  <ListItemText primary={title} />
                                </MenuItem>
                              ),
                            )}
                          </Select>
                          {/* Selected terms list */}
                          {(field.value || []).length > 0 && (
                            <Box
                              sx={{
                                mt: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                              }}
                            >
                              {(field.value || []).map(
                                (id: string | undefined, index: number) => {
                                  const term = (
                                    selectedClient?.terms ?? []
                                  )?.find((term: any) => term.id === id);

                                  return (
                                    <Box
                                      key={id}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        backgroundColor: "action.hover",
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {index + 1}. {term?.title}
                                      </Typography>
                                    </Box>
                                  );
                                },
                              )}
                            </Box>
                          )}
                        </>
                      )}
                    />

                    <FormHelperText>
                      {generalT(`${errors.terms?.message ?? ""}`)}
                    </FormHelperText>
                  </FormControl>

                  {/* zatca reference */}
                  {isConnectedToZatca &&
                    invoiceData?.data?.zatca?.invoice_number && (
                      <Box mt={3}>
                        <Typography mb={1}>
                          {salesT("zatcaReference")}
                        </Typography>
                        <Typography
                          fontSize={"14px"}
                          fontWeight={600}
                          color={"#344054"}
                        >
                          {invoiceData.data.zatca.invoice_number}
                        </Typography>
                      </Box>
                    )}

                  {/* files */}
                  {mode === "add" && (
                    <Box width={"100%"} mt={2}>
                      <FilePondUploader
                        onUpload={(e) => setFiles(e)}
                        maxFiles={3}
                        maxFileSize={3}
                        acceptedFileTypes={[
                          "image/*",
                          "application/pdf",
                          "text/csv",
                          "application/vnd.ms-excel",
                          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        ]}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>

              {/* footer actions */}
              <Stack
                spacing={2}
                direction={"row"}
                mt={3}
                justifyContent={"flex-end"}
              >
                <Button
                  variant="outlined"
                  color="tertiary"
                  onClick={navigateToInvoices}
                  sx={{ minWidth: "180px" }}
                >
                  {generalT("back")}
                </Button>
                {invoiceStatus !== "cancelled" &&
                invoiceStatus !== "submitted" ? (
                  <>
                    {!invoiceData || invoiceStatus === "draft" ? (
                      <LoadingButton
                        variant="contained"
                        type="submit"
                        loading={isPending || isPendingUpdate || isFetching}
                        sx={{ minWidth: "180px" }}
                        disabled={watch("items")?.length === 0}
                        onClick={() => setIsSaveAsDraft(true)}
                      >
                        {generalT("saveAsDraft")}
                      </LoadingButton>
                    ) : null}

                    <LoadingButton
                      variant="contained"
                      type="submit"
                      loading={isPending || isPendingUpdate || isFetching}
                      sx={{ minWidth: "180px" }}
                      disabled={watch("items")?.length === 0 || isDisabled}
                      onClick={() => setIsSaveAsDraft(false)}
                    >
                      {generalT("submit")}
                    </LoadingButton>
                  </>
                ) : null}
              </Stack>
            </form>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default AddEditInvoice;
